import { useEffect, useId, useState } from "react";
import { Clock3, LoaderCircle, LogIn, LogOut, Navigation, Trash2, UserRound } from "lucide-react";
import type { AccountHistoryModel, NavigationHistoryEntry } from "./lib/account";
import "./AccountPanel.css";

interface Props {
  model: AccountHistoryModel;
  onClose: () => void;
  onNavigate: (entry: NavigationHistoryEntry) => void;
}
const date = new Intl.DateTimeFormat("he-IL", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
function distance(meters: number) {
  return meters < 1000 ? `${Math.round(meters)} מ׳` : `${(meters / 1000).toLocaleString("he-IL", { maximumFractionDigits: 1 })} ק״מ`;
}

/** Content only: the parent supplies the modal heading and close/back control. */
export default function AccountPanel({ model, onClose, onNavigate }: Props) {
  const id = useId();
  const [mode, setMode] = useState<"history" | "login" | "register">("history");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [repeatPassword, setRepeatPassword] = useState("");
  const [formError, setFormError] = useState("");
  const [confirmClear, setConfirmClear] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deletePassword, setDeletePassword] = useState("");
  useEffect(() => {
    setMode("history"); setPassword(""); setRepeatPassword(""); setDeletePassword("");
    setConfirmClear(false); setConfirmDelete(false); setFormError("");
  }, [model.user?.id]);
  const switchMode = (next: typeof mode) => {
    setMode(next); setFormError(""); setPassword(""); setRepeatPassword("");
  };
  const authenticate = async (event: React.FormEvent) => {
    event.preventDefault(); setFormError("");
    if (mode === "register" && password !== repeatPassword) { setFormError("הסיסמאות אינן זהות."); return; }
    const success = await model[mode === "register" ? "register" : "login"](username, password);
    setPassword(""); setRepeatPassword("");
    if (success) { setMode("history"); setUsername(""); }
  };

  return <section className="account-panel" dir="rtl" aria-label="חשבון והיסטוריה">
    <div className="account-identity">
      <span className="account-avatar" aria-hidden="true"><UserRound size={23} /></span>
      <div><strong>{model.user ? model.user.username : "ניווט ללא משתמש"}</strong>
        <p>{model.user ? "היסטוריית הניווטים שלך בחשבון" : "הניווטים במצב אורח נשמרים רק במכשיר הזה."}</p></div>
    </div>
    {(model.error || formError) && <div className="account-error" role="alert">
      <p>{formError || model.error}</p>
      {model.error && <button type="button" onClick={() => void model.retry()} disabled={model.busy}>ניסיון נוסף</button>}
    </div>}
    {!model.ready && model.busy && <p className="account-loading" role="status"><LoaderCircle size={18} className="spin" />טוענים את החשבון…</p>}

    {!model.user && mode === "history" && <div className="account-auth-choice">
      <button type="button" className="account-primary" onClick={() => switchMode("login")} disabled={model.busy}><LogIn size={18} />התחברות</button>
      <button type="button" onClick={() => switchMode("register")} disabled={model.busy}>יצירת חשבון</button>
    </div>}
    {!model.user && mode !== "history" && <form className="account-form" onSubmit={event => void authenticate(event)}>
      <h3>{mode === "register" ? "יצירת חשבון" : "התחברות"}</h3>
      <p className="account-hint">ניווטים אחרי ההתחברות נשמרים בחשבון. ההיסטוריה של האורח נשארת במכשיר.</p>
      <label htmlFor={`${id}-username`}>שם משתמש</label>
      <input id={`${id}-username`} name="username" value={username} onChange={event => setUsername(event.target.value)}
        autoComplete="username" autoCapitalize="none" autoCorrect="off" spellCheck={false} dir="ltr"
        minLength={3} maxLength={32} pattern="[A-Za-z0-9_.\-]{3,32}" required disabled={model.busy}
        aria-describedby={`${id}-username-hint`} />
      <small id={`${id}-username-hint`}>3–32 אותיות באנגלית, ספרות, נקודה, מקף או קו תחתון.</small>
      <label htmlFor={`${id}-password`}>סיסמה</label>
      <input id={`${id}-password`} name="password" type="password" value={password} onChange={event => setPassword(event.target.value)}
        autoComplete={mode === "register" ? "new-password" : "current-password"} dir="ltr" minLength={10} maxLength={128} required disabled={model.busy} />
      {mode === "register" && <><small>לפחות 10 תווים.</small>
        <label htmlFor={`${id}-password-repeat`}>סיסמה שוב</label>
        <input id={`${id}-password-repeat`} name="password-confirmation" type="password" value={repeatPassword}
          onChange={event => setRepeatPassword(event.target.value)} autoComplete="new-password" dir="ltr" minLength={10} maxLength={128} required disabled={model.busy} /></>}
      <button type="submit" className="account-primary" disabled={model.busy}>{model.busy ? "רק רגע…" : mode === "register" ? "יצירת חשבון" : "התחברות"}</button>
      <button type="button" className="account-link" onClick={() => switchMode(mode === "register" ? "login" : "register")} disabled={model.busy}>
        {mode === "register" ? "כבר יש לי חשבון" : "אין לי חשבון — הרשמה"}</button>
      <button type="button" className="account-link" onClick={onClose} disabled={model.busy}>המשך ללא משתמש</button>
      <button type="button" className="account-link" onClick={() => switchMode("history")} disabled={model.busy}>חזרה להיסטוריה</button>
    </form>}

    {(model.user || mode === "history") && <>
      <div className="account-history-heading"><h3><Clock3 size={19} />היסטוריית ניווטים</h3>
        {model.entries.length > 0 && <button type="button" className="account-link account-danger" onClick={() => setConfirmClear(true)} disabled={model.busy}>מחיקת היסטוריה</button>}
      </div>
      {confirmClear && <div className="account-confirm" role="group" aria-label="אישור מחיקת היסטוריה">
        <p>{model.user ? "למחוק את כל היסטוריית הניווטים שלך בחשבון?" : "למחוק את היסטוריית הניווטים של האורח במכשיר הזה?"}</p>
        <div><button type="button" className="account-danger" disabled={model.busy} onClick={async () => { if (await model.clearHistory()) setConfirmClear(false); }}>כן, למחוק</button>
          <button type="button" disabled={model.busy} onClick={() => setConfirmClear(false)}>ביטול</button></div>
      </div>}
      {model.ready && !model.busy && model.entries.length === 0 && <p className="account-empty">עדיין אין ניווטים בהיסטוריה. הנסיעה תתווסף כשתתחילו לנווט.</p>}
      <ol className="account-history-list">
        {model.entries.map(entry => <li key={entry.id}>
          <div className="account-trip">
            <time dateTime={new Date(entry.startedAt).toISOString()}>{date.format(entry.startedAt)}</time>
            <strong>{entry.destination.name}</strong>
            {entry.destination.address && <p>{entry.destination.address}</p>}
            <p className="account-trip-origin">מוצא: {entry.origin.label}</p>
            <small>{distance(entry.distanceMeters)} · כ־{Math.max(1, Math.ceil(entry.durationSeconds / 60))} דקות</small>
          </div>
          <div className="account-trip-actions">
            <button type="button" onClick={() => onNavigate(entry)} aria-label={`ניווט אל ${entry.destination.name}`} disabled={model.busy}><Navigation size={17} />ניווט</button>
            <button type="button" className="account-delete-trip" onClick={() => void model.removeEntry(entry.id)} aria-label={`מחיקת הנסיעה אל ${entry.destination.name}`} disabled={model.busy}><Trash2 size={17} /></button>
          </div>
        </li>)}
      </ol>
      {model.user && <div className="account-session-actions">
        <button type="button" onClick={() => void model.logout()} disabled={model.busy}><LogOut size={18} />התנתקות</button>
        <button type="button" className="account-link account-danger" onClick={() => setConfirmDelete(true)} disabled={model.busy}>מחיקת חשבון</button>
      </div>}
      {model.user && confirmDelete && <form className="account-form account-confirm" onSubmit={async event => {
        event.preventDefault();
        const success = await model.deleteAccount(deletePassword);
        setDeletePassword("");
        if (success) { setConfirmDelete(false); setConfirmClear(false); }
      }}>
        <p>החשבון וכל היסטוריית הניווטים שלו יימחקו לצמיתות. ניתן להמשיך לנווט ללא משתמש.</p>
        <label htmlFor={`${id}-delete-password`}>הסיסמה שלך לאישור המחיקה</label>
        <input id={`${id}-delete-password`} type="password" value={deletePassword} onChange={event => setDeletePassword(event.target.value)} autoComplete="current-password" dir="ltr" required maxLength={128} disabled={model.busy} />
        <button type="submit" className="account-danger" disabled={model.busy}>מחיקת החשבון וההיסטוריה לצמיתות</button>
        <button type="button" onClick={() => { setConfirmDelete(false); setDeletePassword(""); }} disabled={model.busy}>ביטול</button>
      </form>}
    </>}
  </section>;
}
